import React, { useEffect, useState, useMemo } from "react";
import { useApp } from "@/context/AppContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Save, FileText, Sheet, MessageCircle, Trash } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/api";
import SearchableSelect from "@/components/SearchableSelect";
import useRevalidate from "@/lib/useRevalidate";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const UNITS = ["kg", "gm", "L", "ml", "pcs", "pack", "dozen", "bag", "box", "bundle"];

export function Purchases() {
  const { t, user } = useApp();
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [supplierName, setSupplierName] = useState("");
  const [supplierId, setSupplierId] = useState(null);
  const [lines, setLines] = useState([{ name_en: "", unit: "kg", quantity: 1, cost: 0, commission_pct: 0, total: 0, item_id: null }]);
  const [paid, setPaid] = useState(0);
  const [newSupOpen, setNewSupOpen] = useState(false);
  const [newSupName, setNewSupName] = useState("");
  const [delId, setDelId] = useState(null);
  const [delBusy, setDelBusy] = useState(false);
  // Month filter chip: null = All, else "YYYY-MM"
  const [monthFilter, setMonthFilter] = useState(null);
  const [supplierFilter, setSupplierFilter] = useState(null);

  // Build unique YYYY-MM buckets from purchase created_at (most recent first).
  const monthChips = useMemo(() => {
    const set = new Set();
    purchases.forEach((p) => { if (p.created_at) set.add(p.created_at.slice(0, 7)); });
    return Array.from(set).sort().reverse().slice(0, 12);
  }, [purchases]);

  const filteredPurchases = useMemo(() => {
    let list = purchases;
    if (monthFilter) list = list.filter((p) => (p.created_at || "").startsWith(monthFilter));
    if (supplierFilter) list = list.filter((p) => (p.supplier_name || "—") === supplierFilter);
    return list;
  }, [purchases, monthFilter, supplierFilter]);

  const filteredTotal = useMemo(() => filteredPurchases.reduce((s, p) => s + Number(p.total || 0), 0), [filteredPurchases]);
  const monthLabel = (ym) => {
    if (!ym) return "";
    const [y, m] = ym.split("-");
    const names = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return `${names[parseInt(m,10)-1]} ${y}`;
  };

  // Per-supplier summary (top 5) respecting the current month filter — supplier chip is independent
  const supplierSummary = useMemo(() => {
    const source = monthFilter ? purchases.filter((p) => (p.created_at || "").startsWith(monthFilter)) : purchases;
    const acc = {};
    source.forEach((p) => {
      const key = (p.supplier_name || "—").trim() || "—";
      if (!acc[key]) acc[key] = { name: key, total: 0, unpaid: 0, count: 0 };
      acc[key].total += Number(p.total || 0);
      acc[key].unpaid += Math.max(0, Number(p.total || 0) - Number(p.amount_paid || 0));
      acc[key].count += 1;
    });
    return Object.values(acc).sort((a, b) => b.total - a.total).slice(0, 5);
  }, [purchases, monthFilter]);

  useEffect(() => {
    api.get("/items").then((r) => setItems(r.data));
    api.get("/suppliers").then((r) => setSuppliers(r.data));
    api.get("/purchases").then((r) => setPurchases(r.data));
  }, []);

  const setLine = (i, patch) => setLines((prev) => prev.map((l, idx) => idx === i ? { ...l, ...patch, total: (patch.quantity ?? l.quantity) * (patch.cost ?? l.cost) * (1 + ((patch.commission_pct ?? l.commission_pct ?? 0) / 100)) } : l));
  const pickCat = (i, id) => { const it = items.find((x) => x.id === id); if (it) setLine(i, { item_id: it.id, name_en: it.name_en, name_gu: it.name_gu, unit: it.unit, cost: it.cost_price || 0 }); };
  const total = useMemo(() => lines.reduce((s, l) => s + (l.total || 0), 0), [lines]);
  const commissionTotal = useMemo(() => lines.reduce((s, l) => s + (Number(l.quantity)||0) * (Number(l.cost)||0) * ((Number(l.commission_pct)||0)/100), 0), [lines]);
  const isOwnerRole = user?.role === "owner";

  const save = async () => {
    for (const l of lines) if (!l.name_en || !l.quantity || !l.cost) return toast.error(t("incomplete_row"));
    if (lines.some((l) => (Number(l.commission_pct)||0) < 0)) return toast.error("Commission cannot be negative");
    try {
      await api.post("/purchases", {
        supplier_id: supplierId, supplier_name: supplierName,
        lines: lines.map((l) => ({
          item_id: l.item_id, name_en: l.name_en, name_gu: l.name_gu || "", unit: l.unit,
          quantity: Number(l.quantity), cost: Number(l.cost),
          commission_pct: Number(l.commission_pct || 0), commission_amount: 0,
          total: Number(l.total),
        })),
        amount_paid: paid, status: paid >= total ? "paid" : paid > 0 ? "partial" : "credit", notes: "",
      });
      toast.success("Saved");
      setLines([{ name_en: "", unit: "kg", quantity: 1, cost: 0, commission_pct: 0, total: 0, item_id: null }]); setPaid(0);
      api.get("/purchases").then((r) => setPurchases(r.data));
      api.get("/items").then((r) => setItems(r.data));
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const openPdf = async (pid) => {
    const token = localStorage.getItem("token");
    const res = await fetch(`${API}/purchases/${pid}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return toast.error("PDF failed");
    const blob = await res.blob();
    window.open(URL.createObjectURL(blob), "_blank");
  };
  const openExcel = async (pid, purchaseNo) => {
    const token = localStorage.getItem("token");
    const res = await fetch(`${API}/purchases/${pid}/excel`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return toast.error("Excel failed");
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `purchase-${purchaseNo || pid}.xlsx`;
    document.body.appendChild(a); a.click(); a.remove();
  };
  const sharePurchaseWA = async (pur) => {
    const msg = `Purchase ${pur.purchase_no} from ${pur.supplier_name || "supplier"} — total ₹${pur.total.toFixed(2)}, status ${pur.status}.`;
    const token = localStorage.getItem("token");
    try {
      if (navigator.canShare && navigator.share) {
        const res = await fetch(`${API}/purchases/${pur.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
        const blob = await res.blob();
        const file = new File([blob], `purchase-${pur.purchase_no}.pdf`, { type: "application/pdf" });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], text: msg, title: `Purchase ${pur.purchase_no}` });
          return;
        }
      }
    } catch (e) { /* fall through to wa.me link */ }
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
    toast.info("Attach the PDF in WhatsApp after the chat opens");
  };

  const doDelete = async () => {
    if (!delId) return;
    setDelBusy(true);
    try {
      const r = await api.delete(`/purchases/${delId}`);
      toast.success(`Deleted ${r.data.purchase_no || ""} — stock reverted`);
      setPurchases((prev) => prev.filter((p) => p.id !== delId));
      // Reload items so stock reflects revert
      api.get("/items").then((res) => setItems(res.data));
      setDelId(null);
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
    finally { setDelBusy(false); }
  };

  if (!isOwnerRole) {
    return <Card className="p-8 text-center text-stone-500" data-testid="purchase-owner-only">Owner only</Card>;
  }

  return (
    <div className="space-y-6 pb-24">
      <h1 className="font-heading text-3xl font-bold">{t("new_purchase")}</h1>

      <Card className="p-4 rounded-2xl">
        <SearchableSelect
          value={supplierId}
          onChange={(id, s) => { setSupplierId(id); setSupplierName(s ? s.label : ""); }}
          options={suppliers.map((s) => ({ id: s.id, label: s.name, sublabel: s.phone }))}
          placeholder={t("supplier")}
          allowCreate
          onCreate={(q) => { setNewSupName(q); setNewSupOpen(true); }}
          createLabel={t("create_new_supplier")}
          testid="pur-supplier-select"
        />
      </Card>

      <div className="space-y-3">
        {lines.map((l, i) => (
          <Card key={i} className="p-4 rounded-2xl" data-testid={`pur-line-${i}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-bold uppercase tracking-widest text-stone-500">#{i + 1}</div>
              <button onClick={() => setLines(lines.filter((_, x) => x !== i))} className="h-8 w-8 rounded-lg bg-red-50 grid place-items-center text-red-600"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="mb-2">
              <SearchableSelect
                value={l.item_id}
                onChange={(id) => id ? pickCat(i, id) : setLine(i, { item_id: null })}
                options={items.map((it) => ({ id: it.id, label: it.name_en, sublabel: it.name_gu }))}
                placeholder={`— ${t("pick_from_catalog")} —`}
                testid={`pur-pick-${i}`}
              />
            </div>
            <div className="mb-2">
              <Input placeholder={t("name_en")} value={l.name_en} onChange={(e) => setLine(i, { name_en: e.target.value })} className="h-12 rounded-lg w-full" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500 block mb-1 sm:hidden">{t("quantity")}</label>
                <Input type="number" placeholder={t("quantity")} value={l.quantity} onChange={(e) => setLine(i, { quantity: parseFloat(e.target.value) || 0 })} className="h-12 rounded-lg font-mono w-full" data-testid={`pur-qty-${i}`} />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500 block mb-1 sm:hidden">{t("unit")}</label>
                <select value={l.unit} onChange={(e) => setLine(i, { unit: e.target.value })} className="h-12 w-full rounded-lg border border-stone-300 px-2">
                  {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500 block mb-1 sm:hidden">{t("cost_price")}</label>
                <Input type="number" placeholder={t("cost_price")} value={l.cost} onChange={(e) => setLine(i, { cost: parseFloat(e.target.value) || 0 })} className="h-12 rounded-lg font-mono w-full" data-testid={`pur-cost-${i}`} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500">Commission %</label>
                <Input type="number" min={0} step="0.01" value={l.commission_pct ?? 0}
                       onChange={(e) => setLine(i, { commission_pct: parseFloat(e.target.value) || 0 })}
                       data-testid={`pur-comm-${i}`} className="h-11 rounded-lg font-mono" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500">Line total</label>
                <div className="h-11 rounded-lg bg-stone-100 grid place-items-center font-mono font-semibold">₹{Number(l.total || 0).toFixed(2)}</div>
              </div>
            </div>
          </Card>
        ))}
        <Button variant="outline" onClick={() => setLines([...lines, { name_en: "", unit: "kg", quantity: 1, cost: 0, commission_pct: 0, total: 0, item_id: null }])} data-testid="pur-add-line" className="w-full h-12 rounded-xl">
          <Plus className="w-4 h-4 mr-2" /> {t("add_row")}
        </Button>
      </div>

      <Card className="p-4 rounded-2xl space-y-2" data-testid="pur-summary">
        <div className="flex justify-between text-sm text-stone-600">
          <span>Commission total</span>
          <span className="font-mono" data-testid="pur-commission-total">₹{commissionTotal.toFixed(2)}</span>
        </div>
        <div className="text-xs font-bold uppercase text-stone-500 mb-1 pt-1">{t("paid")}</div>
        <Input type="number" value={paid} onChange={(e) => setPaid(parseFloat(e.target.value) || 0)} data-testid="pur-paid" className="h-12 rounded-lg" />
        <div className="text-xs text-stone-500" data-testid="pur-payment-method">
          Payment method: <span className="font-semibold text-stone-800">{paid >= total && total > 0 ? "Cash" : paid > 0 ? "Partial (Cash + Credit)" : "Credit"}</span>
        </div>
      </Card>

      <div className="fixed bottom-16 md:bottom-0 left-0 right-0 md:left-auto md:right-6 md:bottom-6 md:w-96 bg-white border-t md:border md:rounded-2xl border-stone-200 p-3 sm:p-4 z-30 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0"><div className="text-[10px] sm:text-xs uppercase tracking-widest text-stone-500">{t("total")}</div><div className="font-mono text-2xl sm:text-3xl font-bold text-stone-900 truncate" data-testid="pur-total">₹{total.toFixed(2)}</div></div>
          <Button onClick={save} data-testid="pur-save" className="h-12 sm:h-14 px-3 sm:px-5 rounded-xl bg-[#166534] hover:bg-[#14532D] text-white font-semibold text-sm sm:text-base whitespace-nowrap"><Save className="w-4 h-4 mr-1 sm:mr-2" /> {t("save")}</Button>
        </div>
      </div>

      <div className="mt-4 space-y-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="font-heading text-lg font-semibold">{t("purchases")}</div>
          <div className="text-xs font-mono text-stone-500" data-testid="pur-filter-summary">
            {filteredPurchases.length} · <span className="font-semibold text-stone-800">₹{filteredTotal.toFixed(2)}</span>
          </div>
        </div>
        {monthChips.length > 0 && (
          <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" data-testid="pur-month-chips">
            <button onClick={() => setMonthFilter(null)} data-testid="pur-chip-all"
                    className={`h-8 px-3 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${monthFilter === null ? "bg-[#B45309] text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"}`}>
              {t("all")} · {purchases.length}
            </button>
            {monthChips.map((ym) => {
              const count = purchases.filter((p) => (p.created_at || "").startsWith(ym)).length;
              return (
                <button key={ym} onClick={() => setMonthFilter(ym)} data-testid={`pur-chip-${ym}`}
                        className={`h-8 px-3 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${monthFilter === ym ? "bg-[#B45309] text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"}`}>
                  {monthLabel(ym)} · {count}
                </button>
              );
            })}
          </div>
        )}
        {supplierSummary.length > 0 && (
          <Card className="p-3 sm:p-4 rounded-2xl bg-[#FFF7ED] border-[#B45309]/20" data-testid="pur-supplier-summary">
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#B45309]">{t("top_suppliers")}</div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500">{monthFilter ? monthLabel(monthFilter) : t("all")}</div>
            </div>
            <div className="space-y-1.5">
              {supplierSummary.map((s) => (
                <button key={s.name} onClick={() => setSupplierFilter(supplierFilter === s.name ? null : s.name)}
                        data-testid={`pur-sup-${s.name}`}
                        className={`w-full flex items-center gap-2 text-sm p-2 -m-1 rounded-lg transition-colors ${supplierFilter === s.name ? "bg-[#B45309]/10 ring-1 ring-[#B45309]/40" : "hover:bg-white/60"}`}>
                  <div className="flex-1 min-w-0 truncate text-left font-medium text-stone-800">{s.name}</div>
                  <div className="text-xs text-stone-500 font-mono whitespace-nowrap">{s.count}×</div>
                  {s.unpaid > 0 && <div className="text-xs font-mono text-red-600 whitespace-nowrap" title={t("unpaid")}>−₹{s.unpaid.toFixed(0)}</div>}
                  <div className="font-mono font-semibold text-stone-900 whitespace-nowrap">₹{s.total.toFixed(2)}</div>
                </button>
              ))}
              {supplierFilter && (
                <button onClick={() => setSupplierFilter(null)} data-testid="pur-sup-clear"
                        className="text-[10px] uppercase font-bold text-[#B45309] hover:underline mt-1">
                  {t("clear")} {t("filter") || "filter"}
                </button>
              )}
            </div>
          </Card>
        )}
        {filteredPurchases.length === 0 && (
          <Card className="p-6 text-center text-sm text-stone-500 rounded-xl" data-testid="pur-empty">
            {monthFilter ? t("no_purchases_in_month") : t("no_purchases")}
          </Card>
        )}
        {filteredPurchases.map((p) => (
          <Card key={p.id} className="p-3 rounded-xl" data-testid={`pur-hist-${p.id}`}>
            <div className="flex justify-between items-center gap-2 flex-wrap">
              <div>
                <div className="font-mono font-semibold">{p.purchase_no}</div>
                <div className="text-xs text-stone-500">{p.supplier_name || "—"} • {p.created_at?.slice(0, 10)} • <span className={`font-semibold ${p.status === "paid" ? "text-[#166534]" : p.status === "credit" ? "text-red-600" : "text-amber-600"}`}>{p.status.toUpperCase()}</span></div>
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <div className="font-mono font-semibold">₹{p.total.toFixed(2)}</div>
                <button onClick={() => openPdf(p.id)} data-testid={`pur-pdf-${p.id}`} title="View PDF" className="h-9 w-9 rounded-lg bg-[#FFF7ED] grid place-items-center text-[#B45309]"><FileText className="w-4 h-4" /></button>
                <button onClick={() => openExcel(p.id, p.purchase_no)} data-testid={`pur-excel-${p.id}`} title="Download Excel" className="h-9 w-9 rounded-lg bg-green-50 grid place-items-center text-[#166534]"><Sheet className="w-4 h-4" /></button>
                <button onClick={() => sharePurchaseWA(p)} data-testid={`pur-wa-${p.id}`} title="Share on WhatsApp" className="h-9 w-9 rounded-lg bg-[#25D366]/10 grid place-items-center text-[#128C7E]"><MessageCircle className="w-4 h-4" /></button>
                <button onClick={() => setDelId(p.id)} data-testid={`pur-del-${p.id}`} title="Delete purchase (reverts stock)" className="h-9 w-9 rounded-lg bg-red-50 grid place-items-center text-red-600"><Trash className="w-4 h-4" /></button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={newSupOpen} onOpenChange={setNewSupOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t("create_new_supplier")}</DialogTitle></DialogHeader>
          <Input value={newSupName} onChange={(e) => setNewSupName(e.target.value)} placeholder={t("name")} data-testid="new-sup-name" className="h-12 rounded-lg" />
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setNewSupOpen(false)}>{t("cancel")}</Button>
            <Button data-testid="new-sup-save" className="bg-[#B45309] hover:bg-[#92400E] text-white"
                    onClick={async () => {
                      if (!newSupName.trim()) return toast.error("Name required");
                      try {
                        const r = await api.post("/suppliers", { name: newSupName.trim(), phone: "", address: "" });
                        setSuppliers((prev) => [...prev, r.data]);
                        setSupplierId(r.data.id); setSupplierName(r.data.name);
                        toast.success("Saved"); setNewSupOpen(false); setNewSupName("");
                      } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
                    }}>{t("save")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("delete_purchase")}</AlertDialogTitle>
            <AlertDialogDescription>{t("delete_purchase_confirm")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="pur-del-cancel">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} disabled={delBusy} data-testid="pur-del-confirm" className="bg-red-600 hover:bg-red-700">
              {t("yes_delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
